import {
    createContext,
    useContext,
    useState,
    type ReactNode,
} from 'react';

interface PrivacyContextType {
    isPrivacyModeEnabled: boolean;
    togglePrivacyMode: () => void;
    setPrivacyMode: (enabled: boolean) => void;
}

const PrivacyContext = createContext<PrivacyContextType | null>(null);

interface PrivacyProviderProps {
    children: ReactNode;
}

function getStoredPrivacyPreference(): boolean {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('privacyMode');
    return stored === 'true';
}

export function PrivacyProvider({ children }: PrivacyProviderProps) {
    const [isPrivacyModeEnabled, setIsPrivacyModeEnabled] = useState<boolean>(
        getStoredPrivacyPreference,
    );

    const togglePrivacyMode = () => {
        setIsPrivacyModeEnabled((prev) => {
            const newValue = !prev;
            localStorage.setItem('privacyMode', String(newValue));
            return newValue;
        });
    };

    const setPrivacyMode = (enabled: boolean) => {
        setIsPrivacyModeEnabled(enabled);
        localStorage.setItem('privacyMode', String(enabled));
    };

    return (
        <PrivacyContext.Provider value={{ isPrivacyModeEnabled, togglePrivacyMode, setPrivacyMode }}>
            {children}
        </PrivacyContext.Provider>
    );
}

export function usePrivacy(): PrivacyContextType {
    const context = useContext(PrivacyContext);
    if (!context) {
        throw new Error('usePrivacy must be used within a PrivacyProvider');
    }
    return context;
}
